import type { HypothesisVerdict } from 'definitions';
import type { LibraryInsight, LibraryTeamRead } from '@@/api/learnings/library-insight';
import type { Tone } from '@/state/learnings/page-view';
import { LEARNING_CHANGE_TYPE_LABELS, LEARNING_PAGE_TYPE_LABELS } from 'definitions';
import { NavLink } from '@/components/NavLink';
import { evidenceNotes } from '@/state/experiments/learnings-notes';
import {
  EXPECTATION_CHOICES,
  NEXT_STEP_CHOICES,
  OUTSIDE_FACTOR_CHOICES,
  labelOf,
} from '@/state/experiments/report-questions';
import { Section, ToneChip } from '../Learnings/parts';

/*
 * The opened Library row, in the Learnings debrief's parts (Section, ToneChip): the same test reads
 * the same in both places, and the row links to the full debrief.
 */

const VERDICT_LABELS: Record<HypothesisVerdict, string> = {
  supported: 'Supports the hypothesis',
  contradicted: 'Contradicts the hypothesis',
  'says-nothing': 'Says nothing about the hypothesis',
};

const VERDICT_TONE: Record<HypothesisVerdict, Tone> = {
  supported: 'pos',
  contradicted: 'neg',
  'says-nothing': 'dim',
};

type InsightProps = { insight: LibraryInsight };

type WhatChangedProps = InsightProps & { variantNames: ReadonlyMap<number, string> };

/** What each variant changed on the page, in the AI card's words. Only for a card that was written. */
export const WhatChanged = ({ insight, variantNames }: WhatChangedProps) => {
  if (insight.status !== 'generated' || insight.variants.length === 0) return null;

  return (
    <Section title="What changed">
      <div className="grid gap-2.5 p-3.5 [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]">
        {insight.variants.map((variant, index) => (
          <div key={variant.variantId} className="border-line flex flex-col gap-2 rounded-card border p-3">
            <span className="micro-label">Variant {index + 1}</span>
            <span className="text-body font-medium">
              {variantNames.get(variant.variantId) ?? `Variant ${variant.variantId}`}
            </span>
            <p className="m-0 text-meta text-pretty">{variant.summary}</p>
            {variant.details && <p className="text-dim m-0 text-meta text-pretty">{variant.details}</p>}
            <div className="flex flex-wrap gap-1.5">
              {variant.changeTypes.map((type) => (
                <ToneChip key={type} tone="dim" className="text-[9px]">
                  {LEARNING_CHANGE_TYPE_LABELS[type]}
                </ToneChip>
              ))}
            </div>
            <span className="supporting">
              {LEARNING_PAGE_TYPE_LABELS[variant.pageType]} · {variant.elementName}
              {variant.themeName && <> · Theme: {variant.themeName}</>}
            </span>
          </div>
        ))}
      </div>
    </Section>
  );
};

/**
 * The sentence on what the test taught, the verdict on the hypothesis, the model's possible
 * reason and suggested next test (cards from prompt version 4 on), and the evidence notes. A card
 * the model could not write says so plainly, and a test with no data says there is nothing to
 * learn from. On a written card the head says the AI wrote it and nobody has reviewed it.
 */
export const WhatItTaught = ({ insight }: InsightProps) => {
  if (insight.status !== 'generated') {
    return (
      <Section title="What it taught">
        <p className="text-dim m-0 p-3.5 text-meta italic">Could not write a learning for this test.</p>
      </Section>
    );
  }

  const notes = evidenceNotes(insight.flags);

  return (
    <Section
      title="What it taught"
      actions={
        <span className="supporting">
          Written by AI from the test&apos;s changes and result. Nobody has reviewed it.
        </span>
      }
    >
      <div className="flex flex-col gap-3 p-3.5">
        {insight.learning ? (
          <p className="m-0 max-w-[70ch] text-[15px] font-medium leading-snug text-pretty">{insight.learning}</p>
        ) : insight.flags.noData ? (
          <p className="text-dim m-0 text-meta">No result to learn from. The test recorded no data.</p>
        ) : (
          // A sentence the model wrote that was thrown away (it stated a figure): the test had data.
          <p className="text-dim m-0 text-meta">No learning could be written for this test.</p>
        )}
        {insight.learning && insight.verdict && (
          <div className="flex flex-wrap gap-1.5">
            <ToneChip tone={VERDICT_TONE[insight.verdict]}>{VERDICT_LABELS[insight.verdict]}</ToneChip>
          </div>
        )}
        {notes.length > 0 && (
          <ul className="text-warn m-0 grid list-none gap-0.5 p-0 text-meta">
            {notes.map((note) => (
              <li key={note.key}>
                {note.experimentId === undefined ? (
                  note.text
                ) : (
                  <>
                    {note.text.split(`#${note.experimentId}`)[0]}
                    <NavLink to={`/experiments/${note.experimentId}`} className="text-accent hover:underline">
                      #{note.experimentId}
                    </NavLink>
                    {note.text.split(`#${note.experimentId}`)[1]}
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
        {(insight.possibleReason || insight.nextTest) && (
          <div className="border-line grid gap-[18px] border-t pt-3 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
            {insight.possibleReason && (
              <div className="flex flex-col gap-1.5">
                <span className="micro-label">Possible reason</span>
                <span className="text-meta text-pretty">{insight.possibleReason}</span>
              </div>
            )}
            {insight.nextTest && (
              <div className="flex flex-col gap-1.5">
                <span className="micro-label">Try next</span>
                <span className="text-meta text-pretty">{insight.nextTest}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </Section>
  );
};

type TeamReadProps = { teamRead: LibraryTeamRead };

/**
 * What the team said in the test's debrief, beside the AI's sentence and never merged into it.
 * Rendered only when there is one. Where the team expected the opposite of what happened, a quiet
 * line says so; the two accounts are not reconciled in the copy.
 */
export const TeamRead = ({ teamRead }: TeamReadProps) => (
  <Section title="The team's read" description={`From the debrief${teamRead.author ? `, by ${teamRead.author}` : ''}`}>
    <div className="flex flex-col gap-2.5 p-3.5">
      {teamRead.learning ? (
        <blockquote className="bg-panel2 border-line m-0 whitespace-pre-line rounded-card border px-3 py-2.5 text-body text-pretty">
          {teamRead.learning}
        </blockquote>
      ) : (
        <p className="text-dim m-0 text-meta">No lesson written.</p>
      )}
      <div className="flex flex-wrap gap-1.5">
        {teamRead.expectation && (
          <ToneChip tone="dim">Expected: {labelOf(EXPECTATION_CHOICES, teamRead.expectation)}</ToneChip>
        )}
        {teamRead.nextStep && <ToneChip tone="dim">Next: {labelOf(NEXT_STEP_CHOICES, teamRead.nextStep)}</ToneChip>}
        {teamRead.outsideFactors.map((factor) => (
          <ToneChip key={factor} tone="warn">
            {labelOf(OUTSIDE_FACTOR_CHOICES, factor)}
          </ToneChip>
        ))}
      </div>
      {teamRead.expectation === 'opposite' && (
        <span className="text-warn text-meta">The team did not expect this result.</span>
      )}
    </div>
  </Section>
);
